import { useSelector, useDispatch } from 'react-redux';

import {
    handleClearRecentlyAdded,
    fetchCartView,
    setAjaxError,
    clearAjaxError,
    setDisableCartAction,
    setRewardsModalState,
} from '../utility/utility-actions';
import { EtrAPI } from '../core/src/etr-api-client';
import EtrAPIClient from '../core/src/etr-wcs-api-client';
import useUserData, { isCallCenter } from './useUserData';
import useAddToCart from './useAddToCart';
import { parseCartResponse } from '../utility/helpers';
import { trackCartMetric, CART_QUANTITY_CHANGE } from '../metrics/track-params';

const eventTrackingConditions = {
    [CART_QUANTITY_CHANGE]: (orderItemId, analyticsData) => {
        try {
            const { quantityChange } = analyticsData || {};
            return quantityChange > 0;
        } catch (e) {}
    },
};

const CART_UPDATE_EVENTS = [CART_QUANTITY_CHANGE];

const conditionallyTrackCartMetrics = (dispatch, analyticsData, orderItemId) => {
    try {
        const filteredEventsToTrack = Array.isArray(CART_UPDATE_EVENTS)
            ? CART_UPDATE_EVENTS.filter(
                  eventName =>
                      typeof eventTrackingConditions[eventName] !== 'function' ||
                      eventTrackingConditions[eventName](orderItemId, analyticsData),
              )
            : null;
        if (!Array.isArray(filteredEventsToTrack) || filteredEventsToTrack.length < 1) {
            return;
        }
        Promise.all(
            filteredEventsToTrack.map(eventToTrack =>
                dispatch(trackCartMetric(eventToTrack, { orderItemId, ...analyticsData })),
            ),
        );
    } catch (e) {}
};

/**
 * Returns the redux state for the Cart
 * @name useCartState
 * @returns {cartInfo, cartId, clearRecentlyAdded, getCartItem}
 */
export default function useCartState(options = {}) {
    const cartInfo = useSelector(state => state.cartInfo);
    const dispatch = useDispatch();
    const { updatedFromService, items, id } = cartInfo || {};
    const cartViewIsLoading = !cartInfo?.cartView;
    const cartDetailsIsLoading = !cartInfo?.cartDetails;
    const emptyCart =
        cartInfo?.cartView?.emptyCart ||
        (cartInfo?.cartView && (cartInfo.cartView.orderItem || []).length === 0) ||
        (updatedFromService && items?.length === 0 && !id);

    return {
        cartInfo,
        cartId: cartInfo && cartInfo.id,
        clearRecentlyAdded: () => dispatch(handleClearRecentlyAdded()),
        getCartItem: sku => cartInfo && cartInfo.items && cartInfo.items.find(item => item.pNum === sku),
        setCartView: calculate => dispatch(fetchCartView(calculate)),
        cartViewIsLoading,
        cartDetailsIsLoading,
        emptyCart,
    };
}

/***
 * params: (autoAction) optional - if passed, will set the modal state based on logged in user state
 */
export const useRewardsModalState = autoAction => {
    const rewardsModalState = useSelector(state => state.cartRewards?.rewardsModalState);
    const { setRewardsModalState } = useCartActions();
    const { cartInfo } = useCartState();
    const { cartView } = cartInfo || {};
    const { x_isLoyaltyEnrolled, x_userType, x_isLoyaltyProductPresent } = cartView || {};
    const isLoggedIn = x_userType === 'R';
    const isLoyaltyEnrolled = x_isLoyaltyEnrolled === 'true';
    const isLoyaltyProductPresent = x_isLoyaltyProductPresent !== 'false';
    const setModalState = autoAction
        ? () => {
              const action = isLoggedIn ? (isLoyaltyEnrolled ? null : 'join') : 'create';
              setRewardsModalState(action);
          }
        : setRewardsModalState;
    return { rewardsModalState, setRewardsModalState: setModalState, isLoyaltyEnrolled, isLoyaltyProductPresent };
};

/***
 * Returns cart actions that will refresh cart view after the action is completed
 */
export const useCartActions = () => {
    const dispatch = useDispatch();
    const orderId = useSelector(state => state.cartInfo?.id);
    const { userData } = useUserData();
    const ccf = isCallCenter(userData);
    const { addToCart } = useAddToCart();
    const commonParams = {
        orderId,
        token: 'NA',
        finalView: 'AjaxOrderItemDisplayView',
        loyaltyAmt: '',
        isLoyaltyEligible: '',
        ccf,
    };

    return {
        setPromoCode: (promotionCode, addPromo, captchaToken) => {
            return EtrAPI.cart
                .setPromoCode({
                    ...commonParams,
                    promoCode: promotionCode,
                    taskType: addPromo ? 'A' : 'R',
                    token: captchaToken,
                })
                .then(resp => {
                    dispatch(fetchCartView(false));
                    return resp;
                });
        },
        setZipCode: zipCode => {
            return EtrAPI.cart.setZipCode({ ...commonParams, zipCode, URL: '/' }).then(resp => {
                dispatch(fetchCartView(false));
                return resp;
            });
        },
        updateCartItem: (orderItemId, qty, options = {}) => {
            const { orderId, ccf } = commonParams;
            const { reFetchCartView = true, analyticsData, ...restOptions } = options;
            const errorClass = 'cart-update-error';
            dispatch(setDisableCartAction(true));
            return EtrAPI.cart
                .updateCartItem(orderItemId, qty, { orderId, ccf, ...restOptions })
                .then(resp => {
                    const parsedResp = parseCartResponse(resp);
                    const { errorMessage } = parsedResp || {};

                    if (errorMessage) {
                        dispatch(setAjaxError(errorMessage, errorClass));
                    } else {
                        dispatch(clearAjaxError(errorClass));
                    }

                    const conditionalEventTracking = cartData => {
                        setTimeout(() => {
                            conditionallyTrackCartMetrics(dispatch, analyticsData, orderItemId);
                        }, 0);
                    };

                    if (reFetchCartView) {
                        return dispatch(fetchCartView(false, true, conditionalEventTracking))
                            .then(resp => {
                                dispatch(setDisableCartAction(false));
                                return parsedResp;
                            })
                            .catch(error => {
                                dispatch(setDisableCartAction(false));
                                return parsedResp;
                            });
                    } else {
                        dispatch(setDisableCartAction(false));
                        //include refreshCart function in the response so that the caller can refresh the cart view if needed
                        return { ...parsedResp, refreshCart: () => dispatch(fetchCartView(false, true)) };
                    }
                })
                .catch(error => {
                    dispatch(setDisableCartAction(false));
                    return Promise.reject(error);
                });
        },
        deleteCartItem: (orderItemId, options = {}) => {
            const { orderId, ccf } = commonParams;
            const { reFetchCartView = true, ...restOptions } = options;
            return EtrAPI.cart.deleteCartItem(orderItemId, { orderId, ccf, ...restOptions }).then(resp => {
                if (reFetchCartView) {
                    dispatch(clearAjaxError());
                    dispatch(fetchCartView(false)).then(() => {
                        return resp;
                    });
                } else {
                    return resp;
                }
            });
        },
        getDeliveryOptions: () => {
            const { orderId } = commonParams;
            return EtrAPIClient.cart.deliveryOptions({ orderId }).then(resp => {
                return resp;
            });
        },
        getHeavyWeightCartItemMessage: () => {
            const espotName = 'CartPage-Oversized-Product';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                return espot?.content;
            });
        },
        getDeliveryOptionsMessage: () => {
            const espotName = 'CartPage-Ship-LearnMoreVISID';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                return espot?.content;
            });
        },
        getSubscriptionPolicies: () => {
            const espotName = 'My-Subscription-Policies-ViewHeadlessCart';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                try {
                    return JSON.parse(espot?.content?.[0]);
                } catch (e) {
                    return [
                        {
                            tooltipProps: {
                                title: 'Return policy BIZTEST',
                                contentSource: 'xitem_prodReturnPolicyTooltip',
                            },
                            label: 'Return policy BIZTEST',
                        },
                        {
                            linkProps: {
                                contentSource: 'xitem_subscriptionstermsofserviceurl',
                                children: 'Terms of service',
                                target: '_blank',
                            },
                        },
                        {
                            linkProps: {
                                to: '/us-en/shop/cv/customerservicechangecancelorder',
                                children: 'Cancellation policy',
                                target: '_blank',
                            },
                        },
                    ];
                }
            });
        },
        getRewardsInfo: () => {
            return EtrAPIClient.cart.get({ profileName: 'HP_LoyaltyRewards_Summary' });
        },
        getTermsAndConditions: () => {
            const espotName = 'OrderPlacementTandCsVisid';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                return espot?.content;
            });
        },
        redeemPoints: points => {
            return EtrAPI.cart.redeemPoints(points).then(resp => {
                dispatch(fetchCartView(false));
                return resp;
            });
        },
        getRewardsBanner: (loggedIn, isRegistered) => {
            const espotName = loggedIn
                ? isRegistered
                    ? 'LoyaltyRegCartPageMsgHeadlessCart'
                    : 'LoyaltyCartPageMsgHeadlessCart'
                : 'LoyaltyGuestCartPageMsgHeadlessCart';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                return espot?.content;
            });
        },
        updateShipMethod: shipModeId => {
            const { orderId, ccf } = commonParams;
            return EtrAPI.cart.updateShipMethod({ orderId, shipModeId, ccf }).then(resp => {
                dispatch(fetchCartView(false));
                return resp;
            });
        },
        updateFreeGiftSelection: (rewardOptionId, items) => {
            return EtrAPI.cart.updateFreeGiftSelection(rewardOptionId, items).then(resp => {
                dispatch(fetchCartView(false));
                return resp;
            });
        },
        updatePaypalShippingAddress: params => {
            const { orderId } = commonParams;
            return EtrAPI.cart.updatePaypalShippingAddress({ ...params, orderId }).then(resp => {
                return resp;
            });
        },

        viewConfig: itemId => {
            const { orderId } = commonParams;
            return EtrAPIClient.cart.viewConfig({ orderId, itemId }).then(resp => {
                return resp;
            });
        },
        verifyCTO: () => {
            const { orderId } = commonParams;
            return EtrAPIClient.cart.verifyCTO({ orderId }).then(resp => {
                return resp;
            });
        },
        getCarePackInfo: () => {
            const { orderId } = commonParams;
            const errorClass = 'carepack-error';
            return EtrAPIClient.cart.carepackInfo({ orderId }).then(resp => {
                const { cartLevelErrorMessages } = resp || {};
                const { message: errorMessage } = cartLevelErrorMessages || {};
                if (errorMessage) {
                    dispatch(setAjaxError(errorMessage, errorClass));
                } else {
                    dispatch(clearAjaxError(errorClass));
                }
                return resp;
            });
        },
        addCrossSell: (product, quantiy, eligibleSKU) => {
            dispatch(setDisableCartAction(true));
            return addToCart(product, quantiy, true, undefined, undefined, eligibleSKU, 'crossSell')
                .then(resp => {
                    dispatch(setDisableCartAction(false));
                    dispatch(fetchCartView(false));
                    return resp;
                })
                .catch(error => {
                    dispatch(setDisableCartAction(false));
                    return Promise.reject(error);
                });
        },
        payPalExpressCheckout: (paypalUrl, returnURL, pageSourceType = 'ShoppingCart') => {
            try {
                const cancelURL = window.location.href;
                const { orderId } = commonParams;
                return EtrAPIClient.cart
                    .paypalExpressCheckout({ paypalUrl, orderId, cancelURL, pageSourceType, returnURL })
                    .then(resp => {
                        const { paymentMessage, paypal_token } = resp;
                        if (paymentMessage == 'NE') {
                            return paypal_token;
                        } else {
                            return Promise.reject();
                        }
                    });
            } catch (e) {
                return Promise.reject(e);
            }
        },
        getAdditionalDeliveryMessages: () => {
            const espotName = 'CartPage-Oversized-Freight';
            return EtrAPIClient.component.get('espot', espotName).then(resp => {
                const [espot] = resp?.[espotName];
                return espot?.content;
            });
        },
        getShareCartLink: () => {
            const { orderId } = commonParams;
            return EtrAPI.cart.getShareCartLink(orderId).then(resp => {
                return resp;
            });
        },
        setLoginCookie: () => {
            try {
                return EtrAPIClient.person.setLoginCookie(window.location.href);
            } catch (e) {}
        },
        setRewardsModalState: action => {
            return dispatch(setRewardsModalState(action));
        },
    };
};
